import { useState, useEffect, useCallback, useRef } from 'react'
import {
  collection,
  query,
  onSnapshot,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  type QueryConstraint,
  type DocumentData,
} from 'firebase/firestore'
import { db, clearFirestoreAndReload } from '@/lib/firebase'
import { updateSyncPath, clearSyncPath } from '@/hooks/useSyncStatus'

function isFirestoreCorruptionError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err)
  return msg.includes('INTERNAL ASSERTION FAILED') || msg.includes('Unexpected state')
}

// Module-level in-session caches. Populated on first load, read synchronously
// on re-mount (tab switches) so the loading spinner never flashes again.
const collectionCache = new Map<string, DocumentData[]>()
const documentCache = new Map<string, DocumentData | null>()

export function useCollection<T extends DocumentData>(
  path: string | null,
  ...constraints: QueryConstraint[]
) {
  const [data, setData] = useState<(T & { id: string })[]>(() =>
    path ? ((collectionCache.get(path) as (T & { id: string })[] | undefined) ?? []) : []
  )
  const [loading, setLoading] = useState(() => (path ? !collectionCache.has(path) : false))
  const [error, setError] = useState<Error | null>(null)

  const constraintsRef = useRef(constraints)
  constraintsRef.current = constraints

  useEffect(() => {
    if (!path) {
      setData([])
      setLoading(false)
      return
    }

    // Handle path changing post-mount (e.g. null → real path after auth resolves)
    const hasCached = collectionCache.has(path)
    if (hasCached) {
      setData(collectionCache.get(path) as (T & { id: string })[])
      setLoading(false)
    }

    // Firestore silently waits when offline with no cache — fail immediately instead
    if (!navigator.onLine && !hasCached) {
      setLoading(false)
      setError(new Error('offline'))
    }

    const q = query(collection(db, path), ...constraintsRef.current)
    let unsubscribe: (() => void) | null = null
    try {
      unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const docs = snapshot.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          })) as (T & { id: string })[]
          collectionCache.set(path, docs)
          setData(docs)
          updateSyncPath(path, snapshot.metadata.hasPendingWrites)
          if (!snapshot.metadata.fromCache || navigator.onLine) setError(null)
          setLoading(false)
        },
        (err) => {
          if (isFirestoreCorruptionError(err)) {
            clearFirestoreAndReload().then((willReload) => {
              if (!willReload) { setError(err); setLoading(false) }
            })
            return
          }
          setError(err)
          setLoading(false)
        },
      )
    } catch (err) {
      if (isFirestoreCorruptionError(err)) {
        clearFirestoreAndReload().then((willReload) => {
          if (!willReload) { setError(err as Error); setLoading(false) }
        })
      } else {
        setError(err as Error)
        setLoading(false)
      }
    }

    // When network comes back, immediately clear error and show spinner
    // so there's no "Failed to load" flash while Firestore reconnects.
    const handleOnline = () => {
      setError(null)
      if (!collectionCache.has(path)) setLoading(true)
    }
    window.addEventListener('online', handleOnline)

    return () => {
      clearSyncPath(path)
      unsubscribe?.()
      window.removeEventListener('online', handleOnline)
    }
  }, [path])

  return { data, loading, error }
}

export function useDocument<T extends DocumentData>(path: string | null) {
  const [data, setData] = useState<(T & { id: string }) | null>(() =>
    path ? ((documentCache.get(path) as (T & { id: string }) | undefined) ?? null) : null
  )
  const [loading, setLoading] = useState(() => (path ? !documentCache.has(path) : false))
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!path) {
      setData(null)
      setLoading(false)
      return
    }

    // Handle path changing post-mount
    const hasCached = documentCache.has(path)
    if (hasCached) {
      setData((documentCache.get(path) as (T & { id: string }) | null) ?? null)
      setLoading(false)
    }

    // Firestore silently waits when offline with no cache — fail immediately instead
    if (!navigator.onLine && !hasCached) {
      setLoading(false)
      setError(new Error('offline'))
    }

    let unsubscribe: (() => void) | null = null
    try {
      unsubscribe = onSnapshot(
        doc(db, path),
        (snapshot) => {
          const value = snapshot.exists()
            ? ({ id: snapshot.id, ...snapshot.data() } as T & { id: string })
            : null
          documentCache.set(path, value)
          setData(value)
          updateSyncPath(path, snapshot.metadata.hasPendingWrites)
          if (!snapshot.metadata.fromCache || navigator.onLine) setError(null)
          setLoading(false)
        },
        (err) => {
          if (isFirestoreCorruptionError(err)) {
            clearFirestoreAndReload().then((willReload) => {
              if (!willReload) { setError(err); setLoading(false) }
            })
            return
          }
          setError(err)
          setLoading(false)
        },
      )
    } catch (err) {
      if (isFirestoreCorruptionError(err)) {
        clearFirestoreAndReload().then((willReload) => {
          if (!willReload) { setError(err as Error); setLoading(false) }
        })
      } else {
        setError(err as Error)
        setLoading(false)
      }
    }

    // When network comes back, immediately clear error and show spinner
    // so there's no "Failed to load" flash while Firestore reconnects.
    const handleOnline = () => {
      setError(null)
      if (!documentCache.has(path)) setLoading(true)
    }
    window.addEventListener('online', handleOnline)

    return () => {
      clearSyncPath(path)
      unsubscribe?.()
      window.removeEventListener('online', handleOnline)
    }
  }, [path])

  return { data, loading, error }
}

function stripUndefined(obj: DocumentData): DocumentData {
  const clean: DocumentData = {}
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined) continue
    if (Array.isArray(value)) {
      clean[key] = value.map((item) =>
        typeof item === 'object' && item !== null && !('toDate' in item)
          ? stripUndefined(item as DocumentData)
          : item,
      )
    } else {
      clean[key] = value
    }
  }
  return clean
}

export function useFirestoreActions(basePath: string) {
  const add = useCallback(
    async (data: DocumentData) => {
      if (!basePath) throw new Error('useFirestoreActions: basePath is empty')
      const ref = await addDoc(collection(db, basePath), {
        ...stripUndefined(data),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      return ref.id
    },
    [basePath],
  )

  const update = useCallback(
    async (id: string, data: DocumentData) => {
      if (!basePath) throw new Error('useFirestoreActions: basePath is empty')
      await updateDoc(doc(db, basePath, id), {
        ...stripUndefined(data),
        updatedAt: serverTimestamp(),
      })
    },
    [basePath],
  )

  const remove = useCallback(
    async (id: string) => {
      if (!basePath) throw new Error('useFirestoreActions: basePath is empty')
      await deleteDoc(doc(db, basePath, id))
    },
    [basePath],
  )

  return { add, update, remove }
}

export { orderBy, query } from 'firebase/firestore'
